import { useRef, useState } from 'react';
import { useIsMutating, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { MultiValue, OnChangeValue, SingleValue } from 'react-select';
import { RequiredFeatureEnabled } from 'src/components/auth';
import { FormInput, handleErrorToast } from 'src/components/molecules';
import { DefaultOption } from 'src/components/molecules/DropdownSelect';
import {
  CaseScreenHeaderDetailsColumnNew,
  HeaderSectionTitle,
  HeaderSectionTitleContainer,
} from 'src/components/organismsLegacy/CaseScreenHeader/CaseScreenHeader.styles';
import { onMutateCaseStatus } from 'src/components/organismsLegacy/CreateReportHeader/useCaseStatus';
import { caseStatusAndCategoriesPanel } from 'src/components/pages/Case/Case.slice';
import { caseCategoryUpdated } from 'src/components/pages/Case/actions/caseCategoryUpdated';
import {
  CaseByIdQuery,
  ReportStatus,
  useAddCaseCategoryMutation,
  useCountriesQuery,
  useRegionsForOrganisationQuery,
  useRemoveCaseCategoryMutation,
  useSetCaseCountryRegionMutation,
  useUpdateCaseStatusMutation,
} from 'src/graphql';
import { useIsFeatureEnabled } from 'src/hooks/useIsFeatureEnabled';
import { AppDispatch } from 'src/store';
import {
  CaseTypeEnum,
  FeatureFlag,
  FormInputVariant,
  FormState,
} from 'src/types';
import { isCloseBlockedByCountryRegion } from 'src/utils/case/isCloseBlockedByCountryRegion';
import { reportStatusToLabel } from 'src/utils/case/reportStatusToLabel';
import {
  CaseStatusAndCategoriesPanelContainer,
  SubmissionRouteLabel,
  SubmissionRouteRow,
  SubmissionRouteValue,
} from './CaseStatusAndCategoriesPanel.styles';

import { FlexViewPrimitive } from 'src/components/layout/FlexView/FlexView.styles';
import { State } from '../../../types';

const BLANK_OPTION: DefaultOption = { value: '', label: '—' };

const caseStatuses = [
  ReportStatus.Read,
  ReportStatus.Investigating,
  ReportStatus.Closed,
];

export interface CaseStatusAndCategoriesPanelProps {
  caseType: CaseTypeEnum;
  submissionRoute?: string;
}

export const CaseStatusAndCategoriesPanel = ({
  caseType,
  submissionRoute,
}: CaseStatusAndCategoriesPanelProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const {
    caseId,
    status,
    selectedCategories,
    activeCategories,
    permissions,
    selectedCountryCode,
    selectedRegionId,
  } = useSelector(caseStatusAndCategoriesPanel);

  const updateCaseStatus = useUpdateCaseStatusMutation({
    onError: () => {
      throw new Error('Unable to update status');
    },
    onMutate: onMutateCaseStatus({ id: caseId, queryClient }),
    onSettled: () =>
      queryClient.invalidateQueries(['CaseById', { id: caseId }]),
  });

  const addCaseCategory = useAddCaseCategoryMutation();
  const removeCaseCategory = useRemoveCaseCategoryMutation();

  const isStatusMutating = useIsMutating(['UpdateCaseStatus']) > 0;
  const isCategoryMutating =
    useIsMutating(['AddCaseCategory']) + useIsMutating(['RemoveCaseCategory']) >
    0;

  const isCountryRegionEnabled = useSelector((state: State) =>
    state.features?.some(
      (f) => f.key === FeatureFlag.COUNTRIES_AND_REGIONS && f.enabled,
    ),
  );

  const { data: countriesData } = useCountriesQuery(undefined, {
    enabled: isCountryRegionEnabled,
  });
  const { data: regionsData } = useRegionsForOrganisationQuery(undefined, {
    enabled: isCountryRegionEnabled,
  });
  const [countryCode, setCountryCode] = useState(selectedCountryCode);
  const [regionId, setRegionId] = useState(selectedRegionId);

  const mutationIdRef = useRef(0);
  const setCaseCountryRegion = useSetCaseCountryRegionMutation({
    onMutate: async ({ input }) => {
      await queryClient.cancelQueries(['CaseById', { id: caseId }]);
      queryClient.setQueryData<CaseByIdQuery | undefined>(
        ['CaseById', { id: caseId }],
        (old) => {
          if (!old?.caseById) return old;
          const newCountry = input.countryCode
            ? (allCountries.find((c) => c.code === input.countryCode) ?? null)
            : null;
          const newRegion = input.regionId
            ? (allRegions.find((r) => r.id === input.regionId) ?? null)
            : null;
          return {
            ...old,
            caseById: {
              ...old.caseById,
              CaseCountryRegion: { country: newCountry, region: newRegion },
            },
          };
        },
      );
    },
    onSettled: () =>
      queryClient.invalidateQueries(['CaseById', { id: caseId }]),
    onError: () =>
      handleErrorToast({ toastMessage: 'Unable to update country/region' }),
  });

  const requireCountryRegionForClose = useIsFeatureEnabled(
    FeatureFlag.CASE_CLOSE_COUNTRY_REGION,
  );
  const isCountryRegionMissing = !countryCode || !regionId;

  const disabled = !permissions.canEditCaseDetails;

  const handleCaseStatusOnChange = (
    option: OnChangeValue<DefaultOption, boolean>,
  ) => {
    if (caseId && option && 'value' in option && option.value) {
      const value = option.value as string;
      if (
        isCloseBlockedByCountryRegion(
          requireCountryRegionForClose,
          isCountryRegionMissing,
          value,
        )
      ) {
        return;
      }
      updateCaseStatus.mutate({
        newCaseStatus: value as ReportStatus,
        caseId,
      });
    }
  };

  const handleCaseCategoryOnChange = (
    selectedOptions: SingleValue<DefaultOption> | MultiValue<DefaultOption>,
  ) => {
    const selectedOptionArray = Array.isArray(selectedOptions)
      ? selectedOptions
      : [selectedOptions];
    const newSelectedCategories = selectedOptionArray.map((option) => ({
      id: option.value,
      name: option.label,
    }));
    dispatch(
      caseCategoryUpdated({
        caseId,
        newSelectedCategories,
        mutationAddCaseCategory: addCaseCategory.mutateAsync,
        mutationRemoveCaseCategory: removeCaseCategory.mutateAsync,
        queryClient,
      }),
    );
  };

  const mutateCaseCountryRegion = (
    nextCountryCode: string | null,
    nextRegionId: string | null,
  ) => {
    if (!caseId) return;

    const id = ++mutationIdRef.current;
    const prevCountryCode = countryCode;
    const prevRegionId = regionId;

    setCountryCode(nextCountryCode);
    setRegionId(nextRegionId);

    setCaseCountryRegion.mutate(
      {
        input: { caseId, countryCode: nextCountryCode, regionId: nextRegionId },
      },
      {
        onError: () => {
          if (mutationIdRef.current !== id) return;
          setCountryCode(prevCountryCode);
          setRegionId(prevRegionId);
        },
      },
    );
  };

  const handleCountryChange = (
    option: OnChangeValue<DefaultOption, boolean>,
  ) => {
    const newCountryCode =
      option && 'value' in option && option.value
        ? (option.value as string)
        : null;

    const firstMatchingRegion = newCountryCode
      ? allRegions.find((r) =>
          r.countries.some((c) => c.code === newCountryCode),
        )
      : null;

    mutateCaseCountryRegion(newCountryCode, firstMatchingRegion?.id ?? null);
  };

  const handleRegionChange = (
    option: OnChangeValue<DefaultOption, boolean>,
  ) => {
    const newRegionId =
      option && 'value' in option && option.value
        ? (option.value as string)
        : null;
    mutateCaseCountryRegion(countryCode, newRegionId);
  };

  const allCountries = countriesData?.countries ?? [];
  const allRegions = regionsData?.regionsForOrganisation ?? [];

  const countryOptions: DefaultOption[] = (() => {
    if (regionId) {
      const selectedRegion = allRegions.find((r) => r.id === regionId);
      return (selectedRegion?.countries ?? []).map((c) => ({
        value: c.code,
        label: c.name,
      }));
    }
    return allCountries.map((c) => ({ value: c.code, label: c.name }));
  })();

  const regionOptions: DefaultOption[] = (() => {
    if (countryCode) {
      return allRegions
        .filter((r) => r.countries.some((c) => c.code === countryCode))
        .map((r) => ({ value: r.id, label: r.name }));
    }
    return allRegions.map((r) => ({ value: r.id, label: r.name }));
  })();

  const selectedCountryOption = countryOptions.find(
    (o) => o.value === countryCode,
  );
  const selectedRegionOption = regionOptions.find((o) => o.value === regionId);

  const inputState = (isLoading = false) => {
    if (!permissions.canViewCaseDetails || isLoading) {
      return FormState.DISABLED;
    }
    if (!permissions.canEditCaseDetails) {
      return FormState.READONLY;
    }
    return FormState.DEFAULT;
  };

  return (
    <CaseScreenHeaderDetailsColumnNew>
      <HeaderSectionTitleContainer>
        <HeaderSectionTitle>Case Attributes</HeaderSectionTitle>
      </HeaderSectionTitleContainer>

      <CaseStatusAndCategoriesPanelContainer>
        {status && (
          <FormInput
            disabled={disabled}
            state={inputState(isStatusMutating)}
            variant={FormInputVariant.DROPDOWN}
            options={caseStatuses.map((status) => ({
              label: reportStatusToLabel(status),
              value: status,
              tooltip: isCloseBlockedByCountryRegion(
                requireCountryRegionForClose,
                isCountryRegionMissing,
                status,
              )
                ? 'Country and region must be set before closing a case'
                : undefined,
            }))}
            value={{
              label: reportStatusToLabel(status as ReportStatus),
              value: status,
            }}
            onChange={(option) => handleCaseStatusOnChange(option)}
            isOptionDisabled={(option) =>
              isCloseBlockedByCountryRegion(
                requireCountryRegionForClose,
                isCountryRegionMissing,
                option.value,
              )
            }
            tooltipText={
              requireCountryRegionForClose && isCountryRegionMissing
                ? 'Country and Region are required before closing a case'
                : undefined
            }
            label="Case Status"
            data-testid="update-case-status-dropdown"
          />
        )}

        <FormInput
          variant={FormInputVariant.DROPDOWN}
          state={inputState(isCategoryMutating)}
          options={activeCategories.map((category) => ({
            value: category.id,
            label: category.name,
          }))}
          value={selectedCategories.map((category) => ({
            value: category.id,
            label: category.name,
          }))}
          onChange={handleCaseCategoryOnChange}
          isMulti
          menuPlacement="bottom"
          label="Case Category"
          data-testid="update-case-category-dropdown"
        />

        <RequiredFeatureEnabled
          requiredFeature={[FeatureFlag.COUNTRIES_AND_REGIONS]}
        >
          <FlexViewPrimitive $gap={2}>
            <FormInput
              variant={FormInputVariant.DROPDOWN}
              state={inputState()}
              options={
                countryCode ? [BLANK_OPTION, ...countryOptions] : countryOptions
              }
              value={selectedCountryOption ?? null}
              onChange={handleCountryChange}
              label="Country"
              isSearchable
              data-testid="update-case-country-dropdown"
              $containerWidth="50%"
            />

            <FormInput
              variant={FormInputVariant.DROPDOWN}
              state={inputState()}
              options={
                regionId ? [BLANK_OPTION, ...regionOptions] : regionOptions
              }
              value={selectedRegionOption ?? null}
              onChange={handleRegionChange}
              label="Region"
              isSearchable
              data-testid="update-case-region-dropdown"
              $containerWidth="50%"
            />
          </FlexViewPrimitive>
        </RequiredFeatureEnabled>

        {caseType === CaseTypeEnum.COI && (
          <SubmissionRouteRow data-testid="case-submission-route">
            <SubmissionRouteLabel>Submission route</SubmissionRouteLabel>
            <SubmissionRouteValue>
              {submissionRoute ?? '–'}
            </SubmissionRouteValue>
          </SubmissionRouteRow>
        )}
      </CaseStatusAndCategoriesPanelContainer>
    </CaseScreenHeaderDetailsColumnNew>
  );
};
